/**
 * Document Activities Hook
 *
 * Wraps the getDocumentActivities Convex query and transforms
 * the raw event data into ActivityEvent shape for the ActivityFeed component.
 */

import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import type { ActivityEvent } from "@/components/ui/ActivityFeed/types";
import { createActivityTransformer } from "./activityHelpers";
import { documentActivityConfig } from "./activityConfigs/document-config";
import type { DocumentActivityType } from "./activityConfigs/document-config";
import type { RawActivity } from "./activityConfigs/types";
import { useWorkspaceQuery } from "@/hooks";

const toActivityEvent = createActivityTransformer(documentActivityConfig);

interface UseDocumentActivitiesReturn {
  activities: ActivityEvent[];
  isLoading: boolean;
}

export function useDocumentActivities(
  documentId: Id<"documents"> | null,
): UseDocumentActivitiesReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getDocumentActivities,
    documentId ? { documentId } : "skip",
  );

  const activities = useMemo<ActivityEvent[]>(() => {
    if (!data) return [];
    return (data as RawActivity<DocumentActivityType>[]).map(toActivityEvent);
  }, [data]);

  return { activities, isLoading };
}
